import React, { useState, useEffect } from 'react';
import { format, differenceInDays, addDays } from 'date-fns';
import { getLeaveBalances, submitLeaveRequest, getLeaveTypes } from '../api/leaveApi';

interface LeaveBalance {
  id: string;
  leave_type_id: string;
  total_days: number;
  used_days: number;
  accrued_days: number;
  leave_types: {
    name: string;
    description: string;
    annual_days: number;
    monthly_accrual_rate: number;
  };
}

export const LeaveRequestForm: React.FC<{ employeeId: string }> = ({ employeeId }) => {
  const [loading, setLoading] = useState(false);
  const [leaveBalances, setLeaveBalances] = useState<LeaveBalance[]>([]);
  const [selectedLeaveType, setSelectedLeaveType] = useState<string>('');
  const [startDate, setStartDate] = useState(format(addDays(new Date(), 1), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(addDays(new Date(), 1), 'yyyy-MM-dd'));
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [halfDayType, setHalfDayType] = useState<'AM' | 'PM'>('AM');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [daysDiff, setDaysDiff] = useState(0);
  const [showSuccessView, setShowSuccessView] = useState(false);
  const formRef = React.useRef<HTMLFormElement>(null);

  useEffect(() => {
    fetchLeaveBalances();
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      const diff = differenceInDays(new Date(endDate), new Date(startDate)) + 1;
      setDaysDiff(diff);
      
      // If dates are different, disable half-day option
      if (diff > 1) {
        setIsHalfDay(false);
      }
    }
  }, [startDate, endDate]);

  // When half-day is selected, ensure start and end dates are the same
  useEffect(() => {
    if (isHalfDay && startDate !== endDate) {
      setEndDate(startDate);
    }
  }, [isHalfDay, startDate]);

  const fetchLeaveBalances = async () => {
    try {
      const balances = await getLeaveBalances(employeeId);
      console.log('Fetched balances:', balances);

      // Add personal leave type if it doesn't exist
      const hasPersonalLeave = balances.some(b => b.leave_types.name === 'PERSONAL_LEAVE');
      console.log('Has personal leave:', hasPersonalLeave);

      if (!hasPersonalLeave) {
        // Get the personal leave type from the API
        const leaveTypes = await getLeaveTypes();
        const personalLeaveType = leaveTypes.find(type => type.name === 'PERSONAL_LEAVE');

        if (personalLeaveType) {
          console.log('Found personal leave type:', personalLeaveType);
          balances.push({
            id: personalLeaveType.id,
            leave_type_id: personalLeaveType.id,
            total_days: 0,
            used_days: 0,
            accrued_days: 0,
            leave_types: {
              name: personalLeaveType.name,
              description: personalLeaveType.description,
              annual_days: personalLeaveType.annual_days,
              monthly_accrual_rate: personalLeaveType.monthly_accrual_rate
            }
          });
        }
      }
      setLeaveBalances(balances);
    } catch (error) {
      console.error('Error fetching leave balances:', error);
      setError('Failed to fetch leave balances');
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const formData = new FormData(event.currentTarget);
      const leaveType = formData.get('leaveType') as string;
      const reason = formData.get('reason') as string;
      const medicalCertificate = formData.get('medicalCertificate') as File;

      // Get the selected leave type details
      const selectedLeaveTypeDetails = leaveBalances.find(b => b.leave_type_id === leaveType);

      console.log('Selected leave type:', leaveType);
      console.log('Selected leave type details:', selectedLeaveTypeDetails);

      // Validate dates
      if (!startDate || !endDate) {
        throw new Error('Please select both start and end dates');
      }

      // Validate medical certificate for sick leave > 2 days
      const needsMedicalCertificate = selectedLeaveTypeDetails?.leave_types.name === 'SICK_LEAVE' && daysDiff > 2;
      if (needsMedicalCertificate && !medicalCertificate) {
        throw new Error('Medical certificate is required for sick leave longer than 2 days');
      }

      await submitLeaveRequest({
        employeeId,
        leaveTypeId: leaveType,
        startDate,
        endDate,
        reason,
        medicalCertificate: needsMedicalCertificate ? medicalCertificate : undefined,
        isHalfDay,
        halfDayType: isHalfDay ? halfDayType : undefined,
      });

      setSuccess('Leave request submitted successfully');
      setShowSuccessView(true);
      fetchLeaveBalances();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to submit leave request');
    } finally {
      setLoading(false);
    }
  };

  const handleNewRequest = () => {
    setShowSuccessView(false);
    formRef.current?.reset();
    setStartDate(format(addDays(new Date(), 1), 'yyyy-MM-dd'));
    setEndDate(format(addDays(new Date(), 1), 'yyyy-MM-dd'));
    setSelectedLeaveType('');
    setIsHalfDay(false);
    setHalfDayType('AM');
    setSuccess('');
  };

  const handleLeaveTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLeaveType(event.target.value);
  };

  const handleHalfDayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setIsHalfDay(isChecked);
    
    // If half-day is selected, ensure start and end dates are the same
    if (isChecked && startDate !== endDate) {
      setEndDate(startDate);
    }
  };

  // Get the selected leave type details
  const selectedLeaveTypeDetails = leaveBalances.find(b => b.leave_type_id === selectedLeaveType);
  const showMedicalCertificateUpload = selectedLeaveTypeDetails?.leave_types.name === 'SICK_LEAVE' && daysDiff > 2;

  if (showSuccessView) {
    return (
      <div className="w-full p-6 bg-white rounded-lg shadow-sm">
        <div className="text-center">
          <div className="mb-4">
            <svg className="mx-auto h-12 w-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Leave Request Submitted Successfully</h3>
          <p className="text-sm text-gray-500 mb-6">
            Your leave request has been submitted and is pending approval.
          </p>
          <button
            onClick={handleNewRequest}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Submit New Request
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-4">
        <h3 className="text-base font-semibold mb-2">Leave Balances</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {leaveBalances
            .filter(balance => balance.leave_types.name !== 'PERSONAL_LEAVE')
            .map((balance) => (
              <div key={balance.id} className="p-2 bg-gray-50 rounded-lg shadow-sm">
                <div className="font-medium text-sm">{balance.leave_types.name}</div>
                <div className="text-xs text-gray-600">
                  Available: {Math.max(0, balance.accrued_days - balance.used_days).toFixed(1)} days
                </div>
              </div>
            ))}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg text-sm">
          {success}
        </div>
      )}

      <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="leaveType" className="block text-sm font-medium text-gray-700 mb-1">
            Leave Type
          </label>
          <select
            id="leaveType"
            name="leaveType"
            required
            value={selectedLeaveType}
            onChange={handleLeaveTypeChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select leave type</option>
            {leaveBalances.map((balance) => (
              <option key={balance.leave_type_id} value={balance.leave_type_id}>
                {balance.leave_types.name}
              </option>
            ))}
          </select>
        </div>

        {selectedLeaveTypeDetails?.leave_types.name === 'SICK_LEAVE' && (
          <div className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
            Note: Medical certificate is required for sick leave longer than 2 days
            {daysDiff > 0 && (
              <div className="mt-1 text-gray-600">
                Current leave duration: {daysDiff} day{daysDiff > 1 ? 's' : ''}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              name="startDate"
              required
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              min={format(new Date(), 'yyyy-MM-dd')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              name="endDate"
              required
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || format(new Date(), 'yyyy-MM-dd')}
              disabled={isHalfDay}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center mb-2">
            <input
              type="checkbox"
              id="halfDay"
              name="halfDay"
              checked={isHalfDay}
              onChange={handleHalfDayChange}
              disabled={startDate !== endDate}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="halfDay" className="ml-2 block text-sm text-gray-700">
              Half-day Leave
            </label>
          </div>
          
          {isHalfDay && (
            <div className="ml-6 mt-2 space-y-2">
              <div className="flex items-center">
                <input
                  type="radio"
                  id="halfDayAM"
                  name="halfDayType"
                  value="AM"
                  checked={halfDayType === 'AM'}
                  onChange={() => setHalfDayType('AM')}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="halfDayAM" className="ml-2 block text-sm text-gray-700">
                  Morning (AM)
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="radio"
                  id="halfDayPM"
                  name="halfDayType"
                  value="PM"
                  checked={halfDayType === 'PM'}
                  onChange={() => setHalfDayType('PM')}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="halfDayPM" className="ml-2 block text-sm text-gray-700">
                  Afternoon (PM)
                </label>
              </div>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
            Reason
          </label>
          <textarea
            id="reason"
            name="reason"
            required={selectedLeaveTypeDetails?.leave_types.name !== 'PERSONAL_LEAVE'}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {showMedicalCertificateUpload && (
          <div>
            <label htmlFor="medicalCertificate" className="block text-sm font-medium text-gray-700 mb-1">
              Medical Certificate (Required)
            </label>
            <input
              type="file"
              id="medicalCertificate"
              name="medicalCertificate"
              accept="image/*,.pdf"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className={`w-full py-2.5 px-4 rounded-md text-white font-medium text-sm ${
            loading
              ? 'bg-blue-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
          }`}
        >
          {loading ? 'Submitting...' : 'Submit Request'}
        </button>
      </form>
    </div>
  );
}; 